import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChevronLeft, ChevronRight, Loader2, Paperclip, MailX } from 'lucide-react'
import { useEmailLogs, useEmailLogFacetas } from '@/hooks/useEmailLog'
import {
  etiquetaEstadoEmail,
  etiquetaTipoEmail,
  opcionesDeFiltroEmail,
} from '@/lib/email-labels'
import { useClienteNombres } from '@/hooks/useClienteNombres'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useAuth0 } from '@/lib/auth'
import { EmailDetailSheet } from '@/components/configuracion/EmailDetailSheet'
import type { EmailLogFilters } from '@/services/emailLogService'

const PAGE_SIZE = 10

/**
 * Tab "Mis mails" del Calendario Fiscal.
 * Muestra los emails enviados por el usuario actualmente autenticado,
 * filtrados por enviado_por_email. No incluye mails de sistema/cron.
 */
export function MisMailsTab() {
  const { organizacionId } = useOrganizacion()
  const { user } = useAuth0()
  const currentUserEmail = user?.email ?? null

  const [filtros, setFiltros] = useState<EmailLogFilters>({
    organizacionId: organizacionId ?? undefined,
    enviadoPor: currentUserEmail ?? undefined,
    limit: PAGE_SIZE,
    offset: 0,
  })
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)

  const { data: logsResult, isLoading } = useEmailLogs(filtros)
  // Las opciones cuentan los mismos envíos que la lista: los del usuario actual.
  const { data: facetas, isError: fallaronFacetas } = useEmailLogFacetas(
    organizacionId ?? undefined,
    filtros.enviadoPor,
  )
  const { clientes, getNombre } = useClienteNombres()

  // Las opciones salen de los valores que la base registró, no de una lista escrita a mano: esta
  // pestaña ofrecía tipos que nunca se enviaron y escondía tres que sí.
  const opcionesTipo = useMemo(
    () =>
      opcionesDeFiltroEmail(
        facetas?.tipos.map((faceta) => faceta.valor),
        filtros.tipo,
        etiquetaTipoEmail,
      ),
    [facetas?.tipos, filtros.tipo],
  )
  const opcionesEstado = useMemo(
    () =>
      opcionesDeFiltroEmail(
        facetas?.estados.map((faceta) => faceta.valor),
        filtros.status,
        (valor) => etiquetaEstadoEmail(valor).label,
      ),
    [facetas?.estados, filtros.status],
  )

  const logs = logsResult?.data ?? []
  const totalLogs = logsResult?.count ?? 0
  const currentPage = Math.floor((filtros.offset ?? 0) / PAGE_SIZE)
  const totalPages = Math.ceil(totalLogs / PAGE_SIZE)

  const handlePageChange = (page: number) => {
    setFiltros((prev) => ({ ...prev, offset: page * PAGE_SIZE }))
  }

  const handleFilterChange = (key: keyof EmailLogFilters, value: string) => {
    setFiltros((prev) => ({ ...prev, [key]: value === '__all__' ? undefined : value, offset: 0 }))
  }

  if (!currentUserEmail) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
        <MailX className="h-8 w-8" />
        <p className="text-sm">No se pudo determinar el usuario actual</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex gap-2">
        <Select
          value={filtros.tipo ?? '__all__'}
          onValueChange={(v) => handleFilterChange('tipo', v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los tipos</SelectItem>
            {opcionesTipo.map(({ valor, label }) => (
              <SelectItem key={valor} value={valor}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filtros.status ?? '__all__'}
          onValueChange={(v) => handleFilterChange('status', v)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los estados</SelectItem>
            {opcionesEstado.map(({ valor, label }) => (
              <SelectItem key={valor} value={valor}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filtros.clienteId ?? '__all__'}
          onValueChange={(v) => handleFilterChange('clienteId', v)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Cliente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los clientes</SelectItem>
            {clientes.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {fallaronFacetas && (
        <p className="text-xs text-destructive">
          No se pudieron cargar las opciones de tipo y estado. Los desplegables quedan vacíos; el
          listado de abajo no está afectado.
        </p>
      )}

      {/* Tabla */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
          <MailX className="h-8 w-8" />
          <p className="text-sm font-medium">No enviaste mails aún</p>
          <p className="text-xs">
            Los mails enviados por vos aparecerán aquí. Los mails anteriores al{' '}
            {new Date().toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}{' '}
            pueden no aparecer si fueron enviados antes de esta funcionalidad.
          </p>
        </div>
      ) : (
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Destinatario</TableHead>
                <TableHead>Asunto</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const statusInfo = etiquetaEstadoEmail(log.status)
                return (
                  <TableRow
                    key={log.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedEmailId(log.id)}
                  >
                    <TableCell className="whitespace-nowrap text-sm">
                      {new Date(log.created_at).toLocaleString('es-AR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell className="text-sm max-w-[160px] truncate">
                      {getNombre(log.cliente_id) ?? (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {etiquetaTipoEmail(log.tipo)}
                    </TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">
                      {log.to?.join(', ')}
                    </TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">
                      <span className="inline-flex items-center gap-1">
                        {log.attachments_meta?.length ? (
                          <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                        ) : null}
                        {log.subject}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                      {log.error && (
                        <p className="text-xs text-destructive mt-1 max-w-[150px] truncate" title={log.error}>
                          {log.error}
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Paginacion */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            {totalLogs} emails en total
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 0}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {currentPage + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages - 1}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <EmailDetailSheet
        emailId={selectedEmailId}
        organizacionId={organizacionId ?? undefined}
        open={!!selectedEmailId}
        onOpenChange={(open) => { if (!open) setSelectedEmailId(null) }}
      />
    </div>
  )
}
